import { useState } from "react";
import { Loader2, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import {
  type Template,
  type WorkProduct,
  type RequiredElement,
  renderWorkProduct,
  workProductMarkdownUrl,
} from "@/lib/api";

export function TemplateTab({
  template,
  workProduct,
  topicSlug,
  onRenderComplete,
}: {
  template: Template;
  workProduct: WorkProduct | null;
  topicSlug: string;
  onRenderComplete: () => void;
}) {
  // Track the ID of a freshly rendered work product so the iframe updates immediately.
  const [renderedId, setRenderedId] = useState<number | null>(null);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Show the freshly rendered version if available; otherwise show the existing work product.
  const displayId = renderedId ?? workProduct?.id ?? null;

  async function handleRender() {
    setRendering(true);
    setError(null);
    try {
      const result = await renderWorkProduct(topicSlug, template.slug);
      if (result.id) setRenderedId(result.id);
      onRenderComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Render failed");
    } finally {
      setRendering(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Required elements + action button */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        {template.required_elements?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {template.required_elements.map((el: RequiredElement) => (
              <span
                key={el.key}
                title={el.question}
                className="inline-flex items-center rounded-full border border-border bg-elevated px-2 py-0.5 text-xs text-muted"
              >
                {el.label}
              </span>
            ))}
          </div>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {displayId ? <DownloadMdButton href={workProductMarkdownUrl(displayId)} /> : null}
          <Button
            size="sm"
            variant={displayId ? "outline" : "default"}
            disabled={rendering}
            onClick={handleRender}
            className="shrink-0"
          >
            {rendering ? (
              <>
                <Loader2 className="size-3 animate-spin" />
                Rendering…
              </>
            ) : displayId ? (
              <>
                <RotateCcw className="size-3" />
                Refresh
              </>
            ) : (
              <>
                <Sparkles className="size-3" />
                Render
              </>
            )}
          </Button>
        </div>
      </div>

      {error ? <p className="text-sm text-red-500">{error}</p> : null}

      {/* Render output — iframe uses the server-side mermaid renderer */}
      {displayId ? (
        <iframe
          key={displayId}
          src={`/api/work-products/${displayId}/view`}
          title={template.name}
          className="w-full rounded-lg border border-border"
          style={{ height: "520px" }}
        />
      ) : !rendering ? (
        <div className="rounded-lg border border-dashed border-border py-8 text-center">
          <p className="text-sm text-muted">Not yet rendered.</p>
          <p className="mt-1 text-xs text-muted">
            Click &ldquo;Render&rdquo; to generate this deliverable from the discussion.
          </p>
        </div>
      ) : (
        <div className="space-y-2 py-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-4 animate-pulse rounded bg-elevated"
              style={{ width: `${65 + (i % 4) * 8}%` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
